import { Component, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Validators } from '@angular/forms';
import { GroupButton } from '@componentes/public-button-group/public-button-group.component';
import { Select } from '@layout/interfaces/select';
import {
	enumAsSelectOptions,
	obter_erro_request,
	renderCoordenada,
	renderNumeroBR,
} from '@utils';
import { DateTimeUtils } from '@utils/datetime-util';
import { ADTColumns } from 'angular-datatables/src/models/settings';
import { ToastrService } from 'ngx-toastr';
import {
	RelatorioDesvio,
	RelatorioDesvioMunicipioDTO,
} from '../../interfaces/response';
import {
	FormularioRelatorio,
	INPUTS_RELATORIOS,
	InstanciaRelatorio,
	PeriodosLabel,
} from '../../interfaces/tipos-relatorios';
import { RelatoriosService } from '../../services/relatorios.service';
import { Subject, takeUntil } from 'rxjs';
import { format } from 'date-fns';
import { PublicTableComponent } from '@componentes/public-table/public-table.component';
import { AgrupamentoEspecial } from '@home/submodulos/dados-meteorologicos/interfaces';
import {
	exportarCSV,
	exportarPDF,
	exportarTXT,
	exportarXLSX,
} from '@home/submodulos/dados-meteorologicos/utils/exportacao-tabelas';

interface OpcaoColuna {
	name: string;
	isFixed: boolean;
}

@Component({
	selector: 'seira-tabela-relatorio-desvio',
	templateUrl: './tabela-relatorio-desvio.component.html',
	styleUrls: ['./tabela-relatorio-desvio.component.scss'],
})
export class TabelaRelatorioDesvioComponent
	implements OnInit, OnDestroy, InstanciaRelatorio
{
	descricaoRelatorio =
		'Define-se como desvio (mm), a diferença entre a pluviometria registrada e a sua climatologia para um mesmo período, em um determinado local ou região. Já o desvio (%) representa a variação da pluviometria em relação à climatologia, expresso em porcentagem, indicando se o acumulado de chuva foi superior ou inferior à climatologia.';
	relatoriosDTO: RelatorioDesvioMunicipioDTO[] = [];
	relatorios: RelatorioDesvio[] = [];
	taCarregando = true;
	private readonly inputs = inject(INPUTS_RELATORIOS);
	@ViewChild('tabelaDesvios', { static: false })
	tabela: PublicTableComponent;

	jaGerouAoMenosUmaVez = false;
	agrupamento: Select<string>[];
	isMunicipioPosto = false;
	opcoesColunas: OpcaoColuna[] = [
		{ name: 'Município/Posto', isFixed: true },
		{ name: 'Desvio (mm)', isFixed: true },
		{ name: 'Desvio (%)', isFixed: true },
		{ name: 'Data', isFixed: true },
		{ name: 'Latitude', isFixed: false },
		{ name: 'Longitude', isFixed: false },
		{ name: 'Altitude', isFixed: false },
		{ name: 'Município', isFixed: false },
		{ name: 'Microrregião', isFixed: false },
		{ name: 'Mesorregião', isFixed: false },
		{ name: 'Região Pluviométrica', isFixed: false },
		{ name: 'Bacia/Sub-bacia', isFixed: false },
	];
	disabledSelectList = false;
	tituloColunaAgrupamento = 'Nome';
	colunasSelecionadas: string[] = [];
	colunas: ADTColumns[];
	periodoTitulo = '';
	tituloTabela = '';
	periodoAgrupamento = '';

	botoesDeExportacao: GroupButton[] = [
		{
			label: '.pdf',
			size: 'small',
			icon: 'ph-file-pdf',
			onClick: () =>
				exportarPDF({
					resultados: this.relatorios,
					tabela: this.tabela,
					periodoAgrupamento: this.periodoAgrupamento,
					tituloTabela: this.tituloTabela,
					colunasAEsquerda:
						this.inputs.form.get(FormularioRelatorio.AGRUPAMENTO)?.value ===
						'MUNICIPIO_POSTO'
							? [0]
							: [],
				}),
		},
		{
			label: '.csv',
			size: 'small',
			icon: 'ph-file-csv',
			onClick: () =>
				exportarCSV({
					tabela: this.tabela,
					periodoAgrupamento: this.periodoAgrupamento,
					tituloTabela: this.tituloTabela,
				}),
		},
		{
			label: '.txt',
			size: 'small',
			icon: 'ph-file-text',
			onClick: () =>
				exportarTXT({
					tabela: this.tabela,
					periodoAgrupamento: this.periodoAgrupamento,
					titulo: this.tituloTabela,
				}),
		},
		{
			label: '.xlsx',
			size: 'small',
			icon: 'ph-file-xls',
			onClick: () =>
				exportarXLSX({
					tabela: this.tabela,
					periodoAgrupamento: this.periodoAgrupamento,
					tituloTabela: this.tituloTabela,
					configsColunas: [
						{ coluna: 'data', usarRender: true },
						{
							coluna: 'desvioMilimetro',
							casasDecimais: 1,
						},
						{
							coluna: 'desvioPorcentagem',
							casasDecimais: 1,
						},
					],
					colunasAEsquerda:
						this.inputs.form.get(FormularioRelatorio.AGRUPAMENTO)?.value ===
						'MUNICIPIO_POSTO'
							? [0]
							: [],
				}),
		},
	];

	private agrupamentoSelecionado: string;
	_destroyed = new Subject();
	tituloArquivoExportacao = '';

	constructor(
		private toastr: ToastrService,
		private readonly relatoriosService: RelatoriosService
	) {
		this.colunasSelecionadas = this.opcoesColunas
			.filter(option => option.isFixed)
			.map(option => option.name);
		this.agrupamento = enumAsSelectOptions(AgrupamentoEspecial);
	}

	ngOnInit(): void {
		this.inputs.form
			.get(FormularioRelatorio.PERIODO_BUSCA)
			?.setValue('mensal', { emitEvent: false });

		this.setValidatorsByAgrupamento(
			this.inputs.form.get(FormularioRelatorio.AGRUPAMENTO)?.value
		);
		this.setValidators();
	}

	ngOnDestroy(): void {
		this.clearValidators();
	}

	setValidatorsByAgrupamento(agrupamentoValue: string) {
		this.handleRemoveValidatorsFromFormControl(FormularioRelatorio.MUNICIPIO);
		this.handleRemoveValidatorsFromFormControl(FormularioRelatorio.ESTACAO);
		this.handleRemoveValidatorsFromFormControl(
			FormularioRelatorio.MICRORREGIAO
		);

		switch (agrupamentoValue) {
			case 'MUNICIPIO_POSTO':
				this.inputs.form
					?.get(FormularioRelatorio.ESTACAO)
					?.setValidators(Validators.required);
				break;
			case 'MICRORREGIAO':
				this.inputs.form
					?.get(FormularioRelatorio.MICRORREGIAO)
					?.setValidators(Validators.required);
				break;
			case 'MUNICIPIO':
				this.inputs.form
					?.get(FormularioRelatorio.MUNICIPIO)
					?.setValidators(Validators.required);
				break;
		}
	}

	setValidators() {
		this.inputs.form
			.get(FormularioRelatorio.AGRUPAMENTO)
			?.valueChanges.pipe(takeUntil(this._destroyed))
			.subscribe({
				next: value => {
					this.setValidatorsByAgrupamento(value);
				},
			});

		this.inputs.form
			.get(FormularioRelatorio.DATA_INICIO)
			?.setValidators(Validators.required);
		this.inputs.form
			.get(FormularioRelatorio.DATA_FIM)
			?.setValidators(Validators.required);
		this.inputs.form
			.get(FormularioRelatorio.PERIODO_BUSCA)
			?.setValidators(Validators.required);
		this.inputs.form
			.get(FormularioRelatorio.AGRUPAMENTO)
			?.setValidators(Validators.required);
	}

	handleRemoveValidatorsFromFormControl(data: FormularioRelatorio) {
		this.inputs.form.get(data)?.clearValidators();
		this.inputs.form.get(data)?.updateValueAndValidity({ emitEvent: false });
	}

	setColunasSelecionadas(colunasSelecionadas: string[]): void {
		this.colunasSelecionadas = colunasSelecionadas;
		this.gerarRelatorio();
	}

	atualizarTituloColuna(): void {
		const agrupamento = this.inputs.form.get(FormularioRelatorio.AGRUPAMENTO)
			?.value;
		this.tituloColunaAgrupamento =
			AgrupamentoEspecial[agrupamento as keyof typeof AgrupamentoEspecial];
	}

	private normalizarAgrupamento(agrupamento: string): string {
		agrupamento = agrupamento.toLowerCase();
		if (agrupamento === 'regiao_pluviometrica') {
			return 'regiaoPluviometrica';
		}
		if (agrupamento === 'subbacia') {
			return 'subBacia';
		}
		if (agrupamento === 'bacia_subbacia') {
			return 'bacia';
		}
		return agrupamento;
	}

	extrairDatasResultado(
		preciptacaoAgrupada: RelatorioDesvio,
		periodoAgrupamento: PeriodosLabel
	) {
		return [
			...new Set(
				preciptacaoAgrupada.desvios
					.filter((el: any) => el.data)
					.map((el: any) => {
						const [ano, mes, dia] = el.data!.split('-');
						return new Date(+ano, (+mes || 1) - 1, +dia || 1);
					})
					.sort((a: any, b: any) => +a - +b)
					.map((data: any) => {
						let formato = 'MM/yyyy';
						if (
							periodoAgrupamento === 'anual' ||
							periodoAgrupamento === 'anual_parcial'
						) {
							formato = 'yyyy';
						}
						return format(data, formato);
					})
			),
		];
	}

	gerarRelatorio(): void {
		this.jaGerouAoMenosUmaVez = true;
		if (this.inputs.form.invalid) {
			return;
		}
		this.relatorios = [];
		const agrupamentoForm = this.inputs.form.get(
			FormularioRelatorio.AGRUPAMENTO
		)?.value;
		const dataInicioForm = this.inputs.form.get(FormularioRelatorio.DATA_INICIO)
			?.value;
		const dataFimForm = this.inputs.form.get(FormularioRelatorio.DATA_FIM)
			?.value;

		this.periodoAgrupamento = this.inputs.form.get(
			FormularioRelatorio.PERIODO_BUSCA
		)?.value;

		const chave: keyof typeof FormularioRelatorio =
			agrupamentoForm === 'MUNICIPIO_POSTO' ? 'ESTACAO' : agrupamentoForm;

		if (
			!agrupamentoForm ||
			!dataInicioForm ||
			!dataFimForm ||
			!this.periodoAgrupamento ||
			!chave
		) {
			return;
		}

		const dataInicioOriginalForm = dataInicioForm;
		const dataFimOriginalForm = new Date(dataFimForm.getTime());

		let dataFimParaTituloDisplay = new Date(dataFimOriginalForm.getTime());

		if (this.periodoAgrupamento === 'anual') {
			const hoje = new Date();
			const anoCorrenteLocal = hoje.getFullYear();
			const mesCorrenteLocal = hoje.getMonth();

			if (dataFimOriginalForm.getFullYear() === anoCorrenteLocal) {
				const anoAjustado =
					mesCorrenteLocal === 0 ? anoCorrenteLocal - 1 : anoCorrenteLocal;
				const mesAjustado = mesCorrenteLocal === 0 ? 11 : mesCorrenteLocal - 1;
				dataFimParaTituloDisplay = new Date(anoAjustado, mesAjustado + 1, 0);

				const formatoData = 'dd/MM/yyyy';
				this.periodoTitulo = `${format(
					dataInicioOriginalForm,
					formatoData
				)} a ${format(dataFimParaTituloDisplay, formatoData)}`;
			} else {
				this.periodoTitulo = DateTimeUtils.formatarDataPeriodo(
					dataInicioOriginalForm,
					dataFimOriginalForm,
					this.periodoAgrupamento
				);
			}
		} else {
			this.periodoTitulo = DateTimeUtils.formatarDataPeriodo(
				dataInicioOriginalForm,
				dataFimOriginalForm,
				this.periodoAgrupamento
			);
		}

		this.taCarregando = true;
		this.inputs.setLoading(true);

		this.agrupamentoSelecionado = this.normalizarAgrupamento(agrupamentoForm);
		this.isMunicipioPosto = agrupamentoForm === 'MUNICIPIO_POSTO';

		const isSearchWithId =
			this.isMunicipioPosto ||
			agrupamentoForm === 'MUNICIPIO' ||
			agrupamentoForm === 'MICRORREGIAO';

		this.tituloTabela = `Desvios - ${this.periodoTitulo}`;
		this.tituloArquivoExportacao = `Desvios - ${this.periodoTitulo}`;

		this.relatoriosService
			.buscarRelatorioDesvio({
				agrupamento: agrupamentoForm,
				idEntidade:
					chave && isSearchWithId
						? this.inputs.form.get(FormularioRelatorio[chave])?.value
						: null,
				dataFim: dataFimForm,
				dataInicio: dataInicioForm,
				periodo: this.periodoAgrupamento.toUpperCase(),
				incluirDetalhes: this.isMunicipioPosto,
			})
			.subscribe({
				next: relatorios => {
					this.relatoriosService.atualizaVisibilidadeBotoesExportacao(
						relatorios
					);
					if (relatorios.length === 0) {
						this.toastr.info(
							'Não há desvios para os parametros selecionados',
							'Sem dados'
						);
					}
					this.atualizarTituloColuna();
					this.atualizarColunasTabelaDesvios();

					const dataResultado = this.extrairDatasResultado(
						relatorios[0],
						this.tipoPeriodo
					);

					relatorios.forEach(data => {
						this.relatorios.push(
							...(this.tipoPeriodo === 'anual' ||
							this.tipoPeriodo === 'anual_parcial'
								? this.getRelatorioDesviosPorAno(
										data,
										dataResultado,
										this.agrupamentoSelecionado
								  )
								: this.formatarResultados(data, this.agrupamentoSelecionado))
						);
					});
				},
				error: err => {
					this.inputs.setLoading(false);
					this.taCarregando = false;
					const msgErro = obter_erro_request(err);
					this.toastr.error(
						msgErro,
						'Erro ao obter informações de desvio no servidor'
					);
				},
				complete: () => {
					this.inputs.setLoading(false);
					this.taCarregando = false;
				},
			});
	}

	private formatarResultados(resultados: any, agrupamento: string) {
		return resultados.desvios.map((resultado: any) => {
			if (agrupamento === 'municipio_posto') {
				return {
					...this.obterDetalhesPosto(resultados),
					data: resultado.data,
					desvioMilimetro: resultado.desvioMilimetro,
					desvioPorcentagem: resultado.desvioPorcentagem,
				};
			}
			return {
				nome: resultados[agrupamento as keyof RelatorioDesvio],
				data: resultado.data,
				desvioMilimetro: resultado.desvioMilimetro,
				desvioPorcentagem: resultado.desvioPorcentagem,
			};
		});
	}

	private getRelatorioDesviosPorAno(
		relatorios: RelatorioDesvio,
		dataResultado: string[],
		agrupamento: string
	) {
		return dataResultado.map(ano => {
			let dadosDoAno = relatorios.desvios.filter((item: any) =>
				item.data.startsWith(ano)
			);

			const currentSystemDate = new Date();
			const currentSystemYear = currentSystemDate.getFullYear();
			const currentSystemMonth = currentSystemDate.getMonth();

			if (parseInt(ano, 10) === currentSystemYear) {
				dadosDoAno = dadosDoAno.filter((item: any) => {
					const itemDateParts = item.data.split('-');
					if (itemDateParts.length < 2) return false;
					const itemMonth = parseInt(itemDateParts[1], 10) - 1;
					return itemMonth < currentSystemMonth;
				});
			}
			const calculos = this.extrairDesviosDeDTOAnual(dadosDoAno);

			return {
				nome: relatorios[agrupamento as keyof RelatorioDesvio],
				data: ano,
				...calculos,
				...(agrupamento === 'municipio_posto'
					? this.obterDetalhesPosto(relatorios)
					: {}),
			};
		});
	}

	private extrairDesviosDeDTOAnual([
		{ desvioMilimetro, desvioPorcentagem },
	]: any[]) {
		return {
			desvioMilimetro,
			desvioPorcentagem,
		};
	}

	private obterDetalhesPosto(dadosPosto: any) {
		return {
			nome:
				dadosPosto.municipio === dadosPosto.estacao
					? dadosPosto.municipio
					: `${dadosPosto.municipio}/${dadosPosto.estacao}`,
			municipio: dadosPosto.municipio || '-',
			latitude: dadosPosto.latitude,
			longitude: dadosPosto.longitude,
			altitude: dadosPosto.altitude,
			microrregiao: dadosPosto.microrregiao || '-',
			mesorregiao: dadosPosto.mesorregiao || '-',
			regiaoPluviometrica: dadosPosto.regiaoPluviometrica || '-',
			bacia: dadosPosto.bacia || '-',
		};
	}

	private get tipoPeriodo(): PeriodosLabel {
		return this.inputs.form.get(FormularioRelatorio.PERIODO_BUSCA)?.value;
	}

	private atualizarColunasTabelaDesvios() {
		const agrupamento = this.inputs.form.get(FormularioRelatorio.AGRUPAMENTO)
			?.value;
		const periodo = this.inputs.form.get(FormularioRelatorio.PERIODO_BUSCA)
			?.value;

		if (agrupamento === 'MUNICIPIO_POSTO') {
			this.disabledSelectList = false;
			const todasColunasDisponiveis = [
				{
					data: 'nome',
					title: 'Município/Posto',
					type: 'string',
					className: 'text-left',
				},
				{
					data: 'latitude',
					title: 'Latitude',
					type: 'number',
					className: 'text-center',
					render: (valor: string) => renderCoordenada(valor),
				},
				{
					data: 'longitude',
					title: 'Longitude',
					type: 'number',
					className: 'text-center',
					render: (valor: string) => renderCoordenada(valor),
				},
				{
					data: 'altitude',
					title: 'Altitude',
					type: 'number',
					className: 'text-center',
					render: (valor: string) => renderCoordenada(valor, 1),
				},
				{
					data: 'microrregiao',
					title: 'Microrregião',
					type: 'string',
					className: 'text-center',
				},
				{
					data: 'mesorregiao',
					title: 'Mesorregião',
					type: 'string',
					className: 'text-center',
				},
				{
					data: 'regiaoPluviometrica',
					title: 'Região Pluviométrica',
					type: 'string',
					className: 'text-center',
				},
				{
					data: 'bacia',
					title: 'Bacia/Sub-bacia',
					type: 'string',
					className: 'text-center',
				},
			];

			const colunasParaExibir = todasColunasDisponiveis.filter(coluna => {
				return this.colunasSelecionadas.includes(coluna.title);
			});

			this.colunas = [...colunasParaExibir];
		} else {
			this.disabledSelectList = true;
			this.colunas = [
				{
					data: 'nome',
					title: this.tituloColunaAgrupamento,
					type: 'string',
					className: 'text-center',
				},
			];
		}

		this.colunas.push(
			...[
				{
					data: 'data',
					title: 'Data',
					type: 'number',
					className: 'text-center',
					render: (value: string) => {
						if (!value) return '-';

						if (periodo === 'mensal') {
							const dataFormatada = new Date(value);
							const mes = String(dataFormatada.getMonth() + 1).padStart(2, '0');
							const ano = dataFormatada.getFullYear();
							return `${mes}/${ano}`;
						}

						return value;
					},
				},
				{
					data: 'desvioPorcentagem',
					title: 'Desvio (%)',
					type: 'number',
					className: 'text-center',
					render: (valor: string) => renderNumeroBR(valor),
				},
				{
					data: 'desvioMilimetro',
					title: 'Desvio (mm)',
					type: 'number',
					className: 'text-center',
					render: (valor: string) => renderNumeroBR(valor),
				},
			]
		);
	}

	private clearValidators(): void {
		this.inputs.form.get(FormularioRelatorio.DATA_INICIO)?.clearValidators();
		this.inputs.form.get(FormularioRelatorio.DATA_FIM)?.clearValidators();
		this.inputs.form.get(FormularioRelatorio.AGRUPAMENTO)?.clearValidators();
		this.inputs.form.get(FormularioRelatorio.MUNICIPIO)?.clearValidators();
		this.inputs.form.get(FormularioRelatorio.ESTACAO)?.clearValidators();
		this.inputs.form.get(FormularioRelatorio.MICRORREGIAO)?.clearValidators();
	}
}
